import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../services/api.client'

export interface ProviderRecord {
	providerId: string
	name: string
	code: string
	description?: string
	logoUrl?: string
	isActive: boolean
	ownerId?: string | null
	lifecycleStatus: string
	dataClassification?: string[]
	businessImpact: string
	riskLevel: string
	metadata?: Record<string, unknown>
	createdAt: string
	updatedAt: string
	deletedAt: string | null
}

export interface ProviderFormData {
	name: string
	code: string
	description?: string
	logoUrl?: string
	isActive?: boolean
	ownerId?: string
	ownerDisplayName?: string // UI-only — display name for the autocomplete
	lifecycleStatus?: string
	dataClassification?: string[]
	businessImpact?: string
	riskLevel?: string
	metadata?: Record<string, unknown>
}

export const useProviders = () =>
	useQuery({
		queryKey: ['providers'],
		queryFn: async () => {
			const response = await apiClient.get<ProviderRecord[]>('/providers')
			return response.data || []
		},
	})

export const useProvider = (providerId: string) =>
	useQuery({
		queryKey: ['providers', providerId],
		queryFn: async () => {
			const response = await apiClient.get<ProviderRecord>(`/providers/${providerId}`)
			return response.data
		},
		enabled: !!providerId,
	})

export const useCreateProvider = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: async (data: ProviderFormData) => {
			const { ownerDisplayName: _, ...payload } = data
			const response = await apiClient.post<ProviderRecord>('/providers', payload)
			return response.data
		},
		onSuccess: () => void qc.invalidateQueries({ queryKey: ['providers'] }),
	})
}

export const useUpdateProvider = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: async ({ id, data }: { id: string; data: Partial<ProviderFormData> }) => {
			const { ownerDisplayName: _, ...payload } = data
			const response = await apiClient.put<ProviderRecord>(`/providers/${id}`, payload)
			return response.data
		},
		onSuccess: () => void qc.invalidateQueries({ queryKey: ['providers'] }),
	})
}

export const useDeleteProvider = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: async (id: string) => {
			await apiClient.delete(`/providers/${id}`)
		},
		onSuccess: () => void qc.invalidateQueries({ queryKey: ['providers'] }),
	})
}
